import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useUserProjectsContext } from '../../contexts/UserProjectsContext';
import { eventBus, useEventSubscribe } from '../../events';
import { firstActionCopyFor, parseAvatar } from '../../utils/avatarProfile';
import { hasSeenTour, isFirstActionDismissed, markFirstActionDismissed } from '../../utils/onboardingFlags';
const DISMISS_LABEL = 'Dismiss';
// Dismissal is per user and shared by every placement of the card, so closing
// the one on the map also clears it from the project list.
const useFirstActionDismissal = (userId) => {
    const [dismissed, setDismissed] = useState(() => (userId ? isFirstActionDismissed(userId) : false));
    useEffect(() => {
        setDismissed(userId ? isFirstActionDismissed(userId) : false);
    }, [userId]);
    useEventSubscribe('onboarding:firstActionDismissed', () => setDismissed(true));
    const dismiss = () => {
        if (userId)
            markFirstActionDismissed(userId);
        setDismissed(true);
        eventBus.emit('onboarding:firstActionDismissed');
    };
    return [dismissed, dismiss];
};
const openCreateProject = () => eventBus.emit('modal:openCreateProject');
// The avatar's first action, floating on the map once the tour is over, for an
// account that has not created a project yet.
export const FirstActionMapCard = () => {
    const { isAuthenticated, user } = useAuth();
    const { projects, hasLoaded } = useUserProjectsContext();
    const userId = user === null || user === void 0 ? void 0 : user.userId;
    const [dismissed, dismiss] = useFirstActionDismissal(userId);
    const [tourDone, setTourDone] = useState(() => (userId ? hasSeenTour(userId) : false));
    const [opened, setOpened] = useState(false);
    useEffect(() => {
        setTourDone(userId ? hasSeenTour(userId) : false);
    }, [userId]);
    useEventSubscribe('onboarding:tourFinished', () => setTourDone(true));
    const eligible = isAuthenticated &&
        !!user &&
        !!userId &&
        user.loginMethod !== 'unverified' &&
        !user.needsUsagePurpose &&
        tourDone &&
        hasLoaded &&
        projects.length === 0;
    if (!eligible || dismissed || opened)
        return null;
    const copy = firstActionCopyFor(parseAvatar(user.avatar));
    return (_jsx("div", { className: "ls-modal-card ls-modal-card-animate fixed z-[55] bottom-10 right-4 w-[min(22rem,calc(100vw-2rem))]", role: "region", "aria-label": copy.headline, children: _jsxs("div", { className: "px-5 py-4", style: { backgroundColor: 'var(--sand)' }, children: [_jsxs("div", { className: "flex items-start justify-between gap-3", children: [_jsx("h3", { className: "ls-emphasis text-base", style: { color: 'var(--forest)' }, children: copy.headline }), _jsx("button", { type: "button", onClick: dismiss, "aria-label": DISMISS_LABEL, title: DISMISS_LABEL, className: "flex-shrink-0 -mt-1 -mr-1 p-1 rounded transition-opacity hover:opacity-70", style: { color: 'var(--pewter)' }, children: _jsx(X, { size: 16 }) })] }), _jsx("p", { className: "text-sm mt-1.5", style: { color: 'var(--charcoal)' }, children: copy.line }), _jsxs("button", { type: "button", onClick: () => {
                        setOpened(true);
                        openCreateProject();
                    }, className: "ls-emphasis mt-4 w-full px-5 py-2.5 text-sm rounded-full flex items-center justify-center gap-2 transition-opacity hover:opacity-90", style: { backgroundColor: 'var(--forest)', color: 'var(--sand)' }, children: [_jsx(Plus, { size: 16 }), copy.buttonLabel] })] }) }));
};
// The avatar's first action in place of the empty My Projects line.
export const FirstActionListCard = ({ dismissedFallback }) => {
    const { user } = useAuth();
    const [dismissed, dismiss] = useFirstActionDismissal(user === null || user === void 0 ? void 0 : user.userId);
    if (!user || user.needsUsagePurpose || dismissed)
        return _jsx(_Fragment, { children: dismissedFallback });
    const copy = firstActionCopyFor(parseAvatar(user.avatar));
    return (_jsxs("div", { className: "mt-3 p-3 rounded-md", style: { backgroundColor: 'var(--charcoal)' }, children: [_jsxs("div", { className: "flex items-start justify-between gap-2", children: [_jsx("h3", { className: "ls-emphasis text-sm", style: { color: 'var(--sand)' }, children: copy.headline }), _jsx("button", { type: "button", onClick: dismiss, "aria-label": DISMISS_LABEL, title: DISMISS_LABEL, className: "flex-shrink-0 -mt-0.5 -mr-0.5 p-0.5 rounded transition-opacity hover:opacity-70", style: { color: 'var(--dew)' }, children: _jsx(X, { size: 14 }) })] }), _jsx("p", { className: "text-xs mt-1", style: { color: 'var(--dew)' }, children: copy.line }), _jsxs("button", { type: "button", onClick: openCreateProject, className: "ls-emphasis mt-3 w-full px-4 py-2 text-xs rounded-full flex items-center justify-center gap-1.5 transition-opacity hover:opacity-90", style: { backgroundColor: 'var(--sand)', color: 'var(--forest)' }, children: [_jsx(Plus, { size: 14 }), copy.buttonLabel] })] }));
};
